/**
 * Enhanced markdown to HTML converter for legal pages
 * Handles the specific formatting used in Terms and Privacy Policy
 */

export function convertMarkdownToHTML(markdown: string): string {
  let html = markdown;

  // Remove the main title (H1) since it's handled by the page component
  html = html.replace(/^# .*$/gm, '');

  // Headers with proper spacing and styling
  html = html.replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold text-gray-900 dark:text-white mt-6 mb-3">$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold text-gray-900 dark:text-white mt-8 mb-4">$1</h2>');

  // Bold text (including the **Effective Date:** pattern)
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900 dark:text-white">$1</strong>');

  // Italic text
  html = html.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 dark:text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');

  // Email links (auto-detect email addresses)
  html = html.replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1" class="text-blue-600 dark:text-blue-400 hover:underline">$1</a>');

  // Handle bullet lists (- or *)
  const lines = html.split('\n');
  const processedLines: string[] = [];
  let inList = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    
    // Check if this is a list item
    if (trimmedLine.match(/^[-*] /)) {
      if (!inList) {
        processedLines.push('<ul class="list-disc list-inside space-y-2 mb-6 ml-4 text-gray-600 dark:text-gray-300">');
        inList = true;
      }
      const listContent = trimmedLine.replace(/^[-*] /, '');
      processedLines.push(`<li class="mb-2">${listContent}</li>`);
    } else {
      // Not a list item
      if (inList) {
        processedLines.push('</ul>');
        inList = false;
      }
      processedLines.push(line);
    }
  }
  
  // Close any remaining list
  if (inList) {
    processedLines.push('</ul>');
  }
  
  html = processedLines.join('\n');

  // Handle numbered lists
  html = html.replace(/^\d+\. (.*$)/gim, '<li class="mb-2">$1</li>');
  
  // Convert paragraphs (double line breaks)
  const paragraphs = html.split('\n\n');
  const processedParagraphs = paragraphs.map(paragraph => {
    const trimmed = paragraph.trim();
    if (!trimmed) return '';
    
    // Skip if it's already wrapped in HTML tags
    if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
      return trimmed;
    }
    
    // Skip if it contains HTML tags (likely already processed)
    if (trimmed.includes('<') && trimmed.includes('>')) {
      return trimmed;
    }
    
    // Wrap in paragraph tags
    return `<p class="mb-4 text-gray-600 dark:text-gray-300">${trimmed.replace(/\n/g, '<br>')}</p>`;
  });

  html = processedParagraphs.join('\n\n');

  // Clean up multiple consecutive line breaks
  html = html.replace(/\n{3,}/g, '\n\n');
  
  // Clean up empty paragraphs
  html = html.replace(/<p class="mb-4 text-gray-600 dark:text-gray-300"><\/p>/g, '');
  html = html.replace(/<p class="mb-4 text-gray-600 dark:text-gray-300">\s*<\/p>/g, '');

  return html;
}

/**
 * Example usage:
 * 
 * const markdownContent = `
 * # Terms of Service
 * 
 * ## 1. Acceptance of Terms
 * 
 * By accessing and using this website, you accept and agree to be bound by the terms and provision of this agreement.
 * 
 * ### 1.1 User Responsibilities
 * 
 * Users must:
 * * Provide accurate information
 * * Maintain account security
 * * Comply with all applicable laws
 * 
 * For questions, contact us at legal@fabrik28.ai
 * `;
 * 
 * const htmlContent = convertMarkdownToHTML(markdownContent);
 */
